import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  OnInit,
  inject,
  input,
  signal,
  computed,
  effect,
  output,
  viewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { authLogout } from '../../core/auth/auth.actions';
import { AuthLoginState } from '../../core/auth/auth.models';
import { UserDTO } from '../../domain/user/user.model';
import { openDialog } from '../dialog/dn-dialog.actions';
import { logo, navigation, navigationSideMenu } from '../navigation/navigation.model';
import { SigninDialogComponent } from '../dialog/signin-dialog/signin-dialog.component';
import { Topic } from '../../domain/topic/topic.model';
import { MatButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBars, faSearch, faUser, faChevronRight, faChevronLeft, faPowerOff, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FormsModule } from '@angular/forms';
import { HeaderBannerComponent, HeaderBannerItem } from '../header-banner/header-banner.component';
import { Content } from '../../domain/content/content.model';

interface MenuItem {
  label: string;
  action: Function;
  routerLink: string;
  logoutIcon?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  imports: [
    RouterLink,
    TitleCasePipe,
    MatButton,
    FaIconComponent,
    FormsModule,
    HeaderBannerComponent,
    TranslatePipe,
  ],
})
export class ToolbarComponent implements OnInit, AfterViewInit {
  NOT_LOGGED_MENU_ITEMS: MenuItem[] = [
    {
      label: 'dottnet.menu.login',
      action: this.openSignin,
      routerLink: undefined,
    },
    {
      label: 'dottnet.menu.signup',
      action: undefined,
      routerLink: '/signup',
    },
    {
      label: 'dottnet.menu.managelog',
      action: undefined,
      routerLink: '/log',
    },
  ];

  LOGGED_MENU_ITEMS: MenuItem[] = [
    {
      label: 'dottnet.menu.profile',
      action: this.openSignin,
      routerLink: 'profile/edit',
    },
    {
      label: 'dottnet.menu.managelog',
      action: undefined,
      routerLink: '/log',
    },
    {
      label: 'dottnet.menu.logout',
      action: this.onLogoutClick,
      routerLink: undefined,
      logoutIcon: true,
    },
  ];

  // Input variables
  // Reference to the outer sidenav template reference
  readonly sidenav = input<MatSidenav>(undefined);
  // Reference to the search field
  searchTextInternal = signal<string>('');
  // Values from app observables
  readonly authLoginState = input<AuthLoginState>(undefined);
  readonly user = input<UserDTO>(undefined);
  readonly topic = input<Topic>(undefined);
  readonly topics = input<Topic[]>([]);
  readonly headerBannerContent = input<Content[]>([]);

  // Only topics of type 1 flagged as visible (isVisible = 1) or sold topics are shown in the toolbar
  readonly visibleTopics = computed<Topic[]>(() => this.topics().filter(topic => (topic.isVisible && topic.topicTypeID === 1) || topic.isSold));

  // Output events
  readonly topicClick = output<Topic>();
  readonly menuClick = output<void>();
  readonly userMenuClick = output<void>();
  readonly searchSubmit = output<string>();

  // Search state
  isSearchOpen = signal(false);

  // Category scroll state
  readonly categoriesContainer = viewChild<ElementRef<HTMLElement>>('categoriesContainer');
  readonly mobileCategoriesContainer = viewChild<ElementRef<HTMLElement>>('mobileCategoriesContainer');
  canScrollLeft = signal(false);
  canScrollRight = signal(true);
  mobileCanScrollLeft = signal(false);
  mobileCanScrollRight = signal(true);

  // References for enums
  readonly AuthLoginState = AuthLoginState;

  // FontAwesome icons
  readonly faBars = faBars;
  readonly faSearch = faSearch;
  readonly faUser = faUser;
  readonly faChevronRight = faChevronRight;
  readonly faChevronLeft = faChevronLeft;
  readonly faPowerOff = faPowerOff;
  readonly faXmark = faXmark;

  // Constants
  navigation: any;
  navigationSideMenu: any;
  logo: string;

  // Header banner items computed from store content
  readonly headerBannerItems = computed<HeaderBannerItem[] | null>(() => {
    const contents = this.headerBannerContent();
    if (!contents || contents.length === 0) {
      return null;
    }
    return contents.map(c => ({
      type: c.template ?? c.descrizioneTopic ?? 'Notizia',
      title: c.titolo,
      link: c.permalink ? `/${c.permalink}` : undefined,
      lastUpdate: this.formatLastUpdate(c.dataCreazione),
    }));
  });

  // Ora/data nel fuso del browser (getter locali): il server manda istanti UTC
  // corretti, la conversione al fuso di chi legge e' compito del client.
  private formatLastUpdate(date: Date | string | undefined): string {
    const dateObj = date ? new Date(date) : new Date();
    const hh = dateObj.getHours().toString().padStart(2, '0');
    const mm = dateObj.getMinutes().toString().padStart(2, '0');
    const now = new Date();
    const isToday =
      dateObj.getFullYear() === now.getFullYear() &&
      dateObj.getMonth() === now.getMonth() &&
      dateObj.getDate() === now.getDate();
    if (isToday) {
      return `Oggi, ore ${hh}:${mm}`;
    }
    const dd = dateObj.getDate().toString().padStart(2, '0');
    const mo = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = dateObj.getFullYear();
    return `${dd}/${mo}/${yyyy} ${hh}:${mm}`;
  }

  userMenuItems: MenuItem[] = [];

  // Observables solo per debuggare
  // sessionID$: Observable<number>;
  // loginType$: Observable<string>;


  private readonly el = inject(ElementRef);
  private readonly document = inject(DOCUMENT);

  constructor(
    private store: Store,
    private route: Router,
  ) {
    effect(() => {
      const userChange = this.user();

      if (!userChange || userChange.idAnagrafica === -1) {
        this.userMenuItems = this.NOT_LOGGED_MENU_ITEMS;
      } else {
        this.userMenuItems = this.LOGGED_MENU_ITEMS;
      }
    });

    effect(() => {
      this.visibleTopics();
      setTimeout(() => {
        this.updateScrollState();
        this.updateMobileScrollState();
      });
    });
  }

  ngOnInit(): void {
    this.navigation = navigation;
    this.navigationSideMenu = navigationSideMenu;
    this.logo = logo;

    //Solo per Debuggare
    // this.sessionID$ = this.store.select(selectSessionID);
    // this.loginType$ = this.store.select(selectGetLoginTypeString);
  }

  ngAfterViewInit(): void {
    // Check initial scroll state after view is ready
    setTimeout(() => {
      this.updateScrollState();
      this.updateMobileScrollState();
    });
  }

  // Desktop scroll methods
  scrollCategoriesLeft(): void {
    const container = this.categoriesContainer()?.nativeElement;
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
      setTimeout(() => this.updateScrollState(), 300);
    }
  }

  scrollCategoriesRight(): void {
    const container = this.categoriesContainer()?.nativeElement;
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
      setTimeout(() => this.updateScrollState(), 300);
    }
  }

  updateScrollState(): void {
    const container = this.categoriesContainer()?.nativeElement;
    if (container) {
      this.canScrollLeft.set(container.scrollLeft > 0);
      this.canScrollRight.set(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  }

  // Mobile scroll methods
  scrollMobileCategoriesLeft(): void {
    const container = this.mobileCategoriesContainer()?.nativeElement;
    if (container) {
      container.scrollBy({ left: -150, behavior: 'smooth' });
      setTimeout(() => this.updateMobileScrollState(), 300);
    }
  }

  scrollMobileCategoriesRight(): void {
    const container = this.mobileCategoriesContainer()?.nativeElement;
    if (container) {
      container.scrollBy({ left: 150, behavior: 'smooth' });
      setTimeout(() => this.updateMobileScrollState(), 300);
    }
  }

  updateMobileScrollState(): void {
    const container = this.mobileCategoriesContainer()?.nativeElement;
    if (container) {
      this.mobileCanScrollLeft.set(container.scrollLeft > 0);
      this.mobileCanScrollRight.set(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  }

  openSignin() {
    this.store.dispatch(
      openDialog({
        content: undefined,
        urlToDownload: undefined,
        urlWithContext: undefined,
        componentType: SigninDialogComponent,
        componentName: 'SigninDialogComponent',
        panelClass: 'dn-dialog-small',
      })
    );
  }

  doAction(item: MenuItem) {
    if (item.action) {
      item.action.apply(this);
    }
  }

  onLogoutClick() {
    this.store.dispatch(authLogout());
  }

  // set on a submit attempt with a too-short key; the message clears itself once the key is valid
  readonly searchSubmitted = signal<boolean>(false);
  readonly showTooShort = computed(() => this.searchSubmitted() && !this.checkMinLength());

  searchTextResult(): void {
    if (this.checkMinLength()) {
      this.route.navigate(['/ricerca', this.searchTextInternal()]);
      this.closeSearch();
    } else {
      this.searchSubmitted.set(true);
    }
  }

  checkMinLength(): boolean {
    return this.searchTextInternal()?.length >= 3;
  }

  getUserName(): string {
    const user = this.user();
    if (!(user?.nome + user?.cognome) || user?.nome === 'Guest') return 'Accedi a DottNet';
    return `${user?.nome} ${user?.cognome}`;
  }

  getUserInitials(): string {
    const user = this.user();
    if (!user || user.nome === 'Guest' || (!user.nome && !user.cognome)) {
      return '';
    }
    const firstInitial = user.nome ? user.nome.charAt(0).toUpperCase() : '';
    const lastInitial = user.cognome ? user.cognome.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  }

  onTopicClick(topic: Topic): void {
    this.route.navigate(['/topic', topic.permalink]);
  }

  onMenuClick(): void {
    this.setLivreaMenuAnchors();
    this.menuClick.emit();
    this.sidenav()?.toggle();
  }

  onUserMenuClick(): void {
    this.setLivreaMenuAnchors();
    this.userMenuClick.emit();
  }

  /**
   * Con la livrea attiva, contenuto e toolbar sono zoomati: i menu non seguono da
   * soli i bottoni. Al click misuriamo la posizione REALE del burger, del bottone
   * profilo e della base della barra, e impostiamo le variabili CSS di ancoraggio.
   *
   * NOTA: nel progetto i drawer sono forzati a `position: fixed` (vedi styles.scss,
   * `.mat-drawer-container > .mat-drawer`), quindi left/top/right sono riferiti al
   * viewport. Le variabili CSS qui sotto sono calcolate in coordinate viewport tramite
   * `getBoundingClientRect()` e funzionano anche con lo zoom della livrea.
   *
   * container li rende anche immuni allo shift della scrollbar quando all'apertura
   * scatta overflow:hidden (container e bottoni traslano insieme).
   * (Le variabili sono usate solo dalle regole .livrea-active in app.component.scss.)
   */
  private setLivreaMenuAnchors(): void {
    const host = this.el.nativeElement as HTMLElement;
    const root = this.document.documentElement;
    const view = this.document.defaultView;
    if (!host || !root || !view) {
      return;
    }
    // Base della barra: da dove partono verticalmente i menu.
    root.style.setProperty('--livrea-menu-top', `${host.getBoundingClientRect().bottom}px`);
    // Ancoriamo ai CONTAINER della toolbar, non ai bottoni (che hanno padding e
    // sfaserebbero i menu): il menu di navigazione al bordo sinistro di .menu-container,
    // il menu profilo al bordo destro di .toolbar-actions. I rect sono gia' in coordinate
    // visive, quindi lo zoom della livrea e' gestito da solo.
    // Menu di navigazione (sinistro): bordo sinistro di .menu-container.
    const menuContainer = host.querySelector('.menu-container') as HTMLElement | null;
    if (menuContainer) {
      root.style.setProperty('--livrea-menu-left', `${menuContainer.getBoundingClientRect().left}px`);
    }
    // Menu profilo (destro): distanza dal bordo destro del viewport al bordo destro di .toolbar-actions.
    const actions = host.querySelector('.toolbar-actions') as HTMLElement | null;
    if (actions) {
      root.style.setProperty('--livrea-menu-right', `${view.innerWidth - actions.getBoundingClientRect().right}px`);
    }
  }

  isSidenavOpen(): boolean {
    return this.sidenav()?.opened ?? false;
  }

  openSearch(): void {
    this.isSearchOpen.set(true);
  }

  closeSearch(): void {
    this.isSearchOpen.set(false);
    this.searchTextInternal.set('');
    this.searchSubmitted.set(false);
  }
}
